import { getAccessToken } from "../../../../core/router/auth";
import type { User } from "../../../../core/types/fe/entities/user";

interface MicrosoftGraphUser {
    id: string;
    givenName: string;
    surname: string;
    mail?: string;
    userPrincipalName?: string;
    "@odata.type"?: string;
}

interface MicrosoftGraphResponse {
    value: MicrosoftGraphUser[];
}

export async function fetchUsers(): Promise<User[]> {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error("User not authenticated");

    // eslint-disable-next-line no-underscore-dangle
    const groupIdsString = window.__RUNTIME_CONFIG__?.GROUP_MEMBERS || "";
    // eslint-disable-next-line no-underscore-dangle
    const groupsUrl = window.__RUNTIME_CONFIG__?.GROUP_MEMBERS_URL || "";
    const groupIds: string[] = groupIdsString.split(",").map((member) => member.trim());

    if (!groupsUrl) {
        throw new Error("GROUP_MEMBERS_URL is not defined in the environment variables");
    }

    const userFetchPromises = groupIds.map(async (groupId) => {
        const url = `${groupsUrl}${groupId}/members`;

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Error fetching data for ${groupId}: ${response.statusText}`);
        }

        const data: MicrosoftGraphResponse = await response.json();

        return data.value
            .filter((user) => user["@odata.type"] === "#microsoft.graph.user")
            .map((user) => ({
                id: user.id,
                userId: user.id,
                firstName: user.givenName,
                lastName: user.surname,
                username: user.mail || user.userPrincipalName,
            }));
    });

    const usersArrays = await Promise.all(userFetchPromises);

    return [...new Map(usersArrays.flat().map((u) => [u.id, u])).values()];
}

export async function fetchUser(userId: string): Promise<User> {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error("User not authenticated");

    const url = `https://graph.microsoft.com/v1.0/users/${userId}`;

    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        throw new Error(`Error fetching user data for ${userId}: ${response.statusText}`);
    }

    const data: MicrosoftGraphUser = await response.json();

    return {
        id: data.id,
        userId: data.id,
        firstName: data.givenName,
        lastName: data.surname,
        username: data.mail || data.userPrincipalName,
    };
}
